// app/frontend/components/ui/Menus/FloatingCornerMenu.tsx
//
// The single floating button in the bottom-right corner. Opens a panel of
// link sections (Admin, Help, ...) so the site never stacks two floating
// buttons in the same corner (#1910). Feature code decides which sections a
// viewer gets; this component only draws them.
import React from 'react';
import { ActionIcon, Divider, Popover, Stack, UnstyledButton, Group } from '@mantine/core';
import { Link } from '@inertiajs/react';
import { IconQuestionMark } from '@tabler/icons-react';
import { ThemedText } from '../../Typography';
import CountBadge from '../Badges/CountBadge';
import { COLORS } from '../../../src/constants/theme';
import { themeVar, themeAlpha } from '../../../src/utils/pageTheme';

export interface FloatingCornerMenuItem {
  icon: React.ComponentType<{ size?: string | number; color?: string }>;
  label: string;
  href: string;
  /** Opens in a new tab with a plain <a> instead of an Inertia visit. */
  external?: boolean;
  /** Shown as a CountBadge beside the label when above zero. */
  count?: number;
}

export interface FloatingCornerMenuSection {
  /** Small heading above the section, e.g. "Admin". Omit for none. */
  title?: string;
  items: FloatingCornerMenuItem[];
}

export interface FloatingCornerMenuProps {
  sections: FloatingCornerMenuSection[];
  /** Accessible name for the icon-only trigger. */
  triggerLabel: string;
  /** Count shown on the trigger itself when above zero. */
  badgeCount?: number;
}

const TRIGGER_SIZE = 52;
const ITEM_HOVER_BG = themeAlpha(0.15);
// White icon on every theme (#2337): a light accent's fill deepens so the
// glyph reads, rather than a pale button behind a white "?".
const TRIGGER_GRADIENT = `linear-gradient(135deg, ${themeVar('white-text-fill')} 0%, color-mix(in srgb, ${themeVar('white-text-fill')} 84%, black) 50%, ${themeVar('white-text-fill')} 100%)`;
const TRIGGER_SHADOW = `0 0 20px ${themeAlpha(0.5)}, 0 4px 12px rgba(0, 0, 0, 0.3)`;
const TRIGGER_SHADOW_HOVER = `0 0 30px ${themeAlpha(0.7)}, 0 6px 16px rgba(0, 0, 0, 0.4)`;

const formatCount = (count: number) => (count > 99 ? '99+' : String(count));

const itemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  borderRadius: 8,
  transition: 'background-color 0.2s ease',
};
const hoverOn = (e: React.MouseEvent<HTMLElement>) => {
  e.currentTarget.style.backgroundColor = ITEM_HOVER_BG;
};
const hoverOff = (e: React.MouseEvent<HTMLElement>) => {
  e.currentTarget.style.backgroundColor = 'transparent';
};

const MenuLink: React.FC<{ item: FloatingCornerMenuItem }> = ({ item }) => {
  const content = (
    <Group gap="sm" px="sm" py={10} justify="space-between" wrap="nowrap">
      <Group gap="sm" wrap="nowrap">
        <span style={{ color: themeVar('accent'), display: 'inline-flex' }}><item.icon size={18} color="currentColor" /></span>
        <ThemedText component="span" size="sm" className="poppins" color={COLORS.textPrimary} style={{ whiteSpace: 'nowrap' }}>
          {item.label}
        </ThemedText>
      </Group>
      {item.count != null && item.count > 0 && (
        <span style={{ flexShrink: 0, display: 'inline-flex' }}>
          <CountBadge count={item.count} ariaLabel={`${item.count} pending`} />
        </span>
      )}
    </Group>
  );

  if (item.external) {
    return (
      <UnstyledButton
        component="a"
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        style={itemStyle}
        onMouseEnter={hoverOn}
        onMouseLeave={hoverOff}
      >
        {content}
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton component={Link} href={item.href} style={itemStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
      {content}
    </UnstyledButton>
  );
};

export const FloatingCornerMenu: React.FC<FloatingCornerMenuProps> = ({ sections, triggerLabel, badgeCount = 0 }) => {
  const visibleSections = sections.filter((section) => section.items.length > 0);

  return (
    <div
      className="floating-corner-menu"
      style={{
        position: 'fixed',
        bottom: 'var(--floating-help-bottom, 24px)',
        right: 'var(--floating-help-right, 24px)',
        zIndex: 1000,
        transition: 'bottom 0.2s ease',
      }}
    >
      <Popover width={250} position="top-end" withArrow shadow="xl">
        <Popover.Target>
          <ActionIcon
            aria-label={triggerLabel}
            size={TRIGGER_SIZE}
            radius="xl"
            variant="filled"
            style={{
              position: 'relative',
              overflow: 'visible',
              background: TRIGGER_GRADIENT,
              border: 'none',
              boxShadow: TRIGGER_SHADOW,
              transition: 'all 0.3s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = TRIGGER_SHADOW_HOVER;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = TRIGGER_SHADOW;
            }}
          >
            <IconQuestionMark size={26} color="#FFF" stroke={2.5} aria-hidden />
            {badgeCount > 0 && (
              <span
                aria-hidden
                data-testid="floating-corner-menu-badge"
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  minWidth: 20,
                  height: 20,
                  padding: '0 5px',
                  borderRadius: 999,
                  backgroundColor: COLORS.danger,
                  color: '#fff',
                  border: `2px solid ${COLORS.pageBg}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  pointerEvents: 'none',
                }}
              >
                {formatCount(badgeCount)}
              </span>
            )}
          </ActionIcon>
        </Popover.Target>

        <Popover.Dropdown
          p={6}
          style={{
            background: `linear-gradient(135deg, ${COLORS.pageBg} 0%, ${COLORS.pageBgDeep} 100%)`,
            border: `1px solid ${themeAlpha(0.4)}`,
            borderRadius: 12,
            boxShadow: `0 0 25px ${themeAlpha(0.25)}, 0 8px 24px rgba(0, 0, 0, 0.5)`,
          }}
        >
          <Stack gap={0}>
            {visibleSections.map((section, index) => (
              <React.Fragment key={section.title ?? index}>
                {index > 0 && <Divider my={4} color={COLORS.borderLight} />}
                {section.title && (
                  <ThemedText
                    tier="quaternary"
                    tt="uppercase"
                    fw={600}
                    style={{ padding: '6px 12px 2px', letterSpacing: '0.08em', fontSize: 11 }}
                  >
                    {section.title}
                  </ThemedText>
                )}
                {section.items.map((item) => (
                  <MenuLink key={item.href} item={item} />
                ))}
              </React.Fragment>
            ))}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};

export default FloatingCornerMenu;
